/**
 * Redeem a single-use invite link and set a password.
 *
 * Rendered **outside** `AuthGate`, because by definition nobody using this page
 * is signed in. It is the same route an owner's invite produces and the same
 * one `python -m controller.bootstrap_admin` prints for the very first
 * operator — deliberately one path rather than two, because a bootstrap flow
 * that is a special case is a flow nobody ever exercises, and the one time it
 * matters is the one time nobody has tested it.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';

import { Gate } from '../components/AuthGate';
import { Callout } from '../components/Callout';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/Form';
import { apiPost, describeError } from '../lib/api';
import { useResource } from '../lib/useResource';

/** The server enforces this; matching it here avoids a round trip to be told. */
const MIN_LENGTH = 12;

interface InviteInfo {
  email: string;
}

export function SetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const invite = useResource<InviteInfo>(
    `/api/auth/invite/${encodeURIComponent(token)}`,
    { enabled: token.length > 0 },
  );

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready = password.length >= MIN_LENGTH && confirm === password && !busy;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiPost('/api/auth/accept-invite', { token, password });
      setDone(true);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Gate title="Set your password">
      <div className="stack">
          {!token ? (
            <Callout tone="warning" title="This link is incomplete">
              It carries no token. Ask an owner for a fresh invite link.
            </Callout>
          ) : null}

          {token && invite.error ? (
            <Callout tone="critical" title="This link is not usable">
              {describeError(invite.error)}
            </Callout>
          ) : null}

          {done ? (
            <>
              <Callout tone="good" title="Password set">
                Any sessions that existed before now have been signed out — a
                reset is only worth doing if it ends whatever prompted it.
              </Callout>
              <div>
                <Button onClick={() => window.location.assign('/')}>
                  Go to the panel
                </Button>
              </div>
            </>
          ) : invite.data ? (
            <form className="stack" onSubmit={submit}>
              <p className="muted">
                Setting the password for <strong>{invite.data.email}</strong>.
                This link works once.
              </p>
              {error ? (
                <Callout tone="critical" live title="Could not set it">
                  {error}
                </Callout>
              ) : null}
              <TextInput
                id="new-password"
                labelText="New password"
                type="password"
                autoComplete="new-password"
                helperText={`At least ${MIN_LENGTH} characters.`}
                invalid={tooShort}
                invalidText={`Too short — at least ${MIN_LENGTH} characters.`}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={busy}
              />
              <TextInput
                id="confirm-password"
                labelText="Confirm password"
                type="password"
                autoComplete="new-password"
                invalid={mismatch}
                invalidText="These do not match."
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                disabled={busy}
              />
              <div>
                <Button type="submit" size="lg" disabled={!ready}>
                  {busy ? 'Setting…' : 'Set password'}
                </Button>
              </div>
            </form>
        ) : token && invite.loading ? (
          <p className="muted">Checking the link…</p>
        ) : null}
      </div>
    </Gate>
  );
}